import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Package, Plus, Search, Edit, Trash2, Eye, X, TrendingUp, DollarSign, Boxes, AlertCircle } from 'lucide-react';

interface Product {
  id: number;
  name: string;
  sku: string;
  price: number;
  cost: number;
  stock: number;
  category: string;
  description?: string;
  companyId?: number;
  statusId?: number;
  createdAt: string;
  updatedAt?: string;
}

const T = {
  title: "المنتجات",
  subtitle: "إدارة المنتجات والمخزون",
  createNew: "إضافة منتج جديد",
  totalProducts: "إجمالي المنتجات",
  totalStock: "إجمالي المخزون",
  totalValue: "القيمة الإجمالية",
  categories: "الفئات",
  search: "بحث باسم المنتج أو SKU...",
  loading: "جاري التحميل...",
  noProducts: "لا توجد منتجات",
  thName: "اسم المنتج",
  thSku: "SKU",
  thPrice: "السعر",
  thCost: "التكلفة",
  thStock: "المخزون",
  thCategory: "الفئة",
  thActions: "الإجراءات",
  view: "عرض",
  edit: "تعديل",
  delete: "حذف",
  editProduct: "تعديل المنتج",
  createProduct: "إضافة منتج جديد",
  viewProduct: "تفاصيل المنتج",
  lblName: "اسم المنتج",
  lblSku: "SKU",
  lblPrice: "السعر",
  lblCost: "التكلفة",
  lblStock: "المخزون",
  lblCategory: "الفئة",
  lblDescription: "الوصف",
  cancel: "إلغاء",
  update: "تحديث",
  create: "إضافة",
  close: "إغلاق",
  confirmDelete: "هل أنت متأكد من حذف هذا المنتج؟",
  updated: "تم تحديث المنتج بنجاح",
  created: "تم إضافة المنتج بنجاح",
  deleted: "تم حذف المنتج بنجاح",
  error: "حدث خطأ",
  lowStock: "مخزون منخفض",
  inStock: "متوفر",
  outOfStock: "نفذ المخزون",
  profit: "الربح",
  margin: "الهامش"
};

export default function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [form, setForm] = useState({
    name: '', sku: '', price: '0', cost: '0',
    stock: '0', category: '', description: ''
  });

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/products?pageSize=100');
      if (res.data?.success) {
        const data = res.data.data;
        setProducts(Array.isArray(data) ? data : (data.items || []));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingProduct(null);
    setForm({ name: '', sku: '', price: '0', cost: '0', stock: '0', category: '', description: '' });
    setShowModal(true);
  };

  const openEdit = (p: Product) => {
    setEditingProduct(p);
    setForm({
      name: p.name || '',
      sku: p.sku || '',
      price: p.price.toString(),
      cost: p.cost.toString(),
      stock: p.stock.toString(),
      category: p.category || '',
      description: p.description || ''
    });
    setShowModal(true);
  };

  const openView = (p: Product) => { setSelectedProduct(p); setShowViewModal(true); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        sku: form.sku,
        price: parseFloat(form.price) || 0,
        cost: parseFloat(form.cost) || 0,
        stock: parseInt(form.stock) || 0,
        category: form.category,
        description: form.description
      };
      if (editingProduct) {
        await api.put('/api/products/' + editingProduct.id, payload);
        alert(T.updated);
      } else {
        await api.post('/api/products', payload);
        alert(T.created);
      }
      setShowModal(false);
      loadProducts();
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(T.confirmDelete)) return;
    try {
      await api.delete('/api/products/' + id);
      loadProducts();
      alert(T.deleted);
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const categories = Array.from(new Set(products.map(p => p.category).filter(Boolean)));
  const filtered = products.filter(p => {
    const matchSearch = (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
                       (p.sku || '').toLowerCase().includes(search.toLowerCase());
    const matchCategory = categoryFilter === 'all' || p.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  const totalStock = products.reduce((sum, p) => sum + (p.stock || 0), 0);
  const totalValue = products.reduce((sum, p) => sum + ((p.price || 0) * (p.stock || 0)), 0);
  const lowStockCount = products.filter(p => p.stock > 0 && p.stock < 20).length;
  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition';

  const getStockStatus = (stock: number) => {
    if (stock === 0) return { label: T.outOfStock, color: 'bg-red-50 text-red-700 border-red-200' };
    if (stock < 20) return { label: T.lowStock, color: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
    return { label: T.inStock, color: 'bg-green-50 text-green-700 border-green-200' };
  };

  const getProfitMargin = (price: number, cost: number) => {
    if (cost === 0) return 0;
    return (((price - cost) / cost) * 100).toFixed(1);
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
            <Package className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>{T.title}</span>
            <p className="text-sm text-gray-500 font-normal mt-1">{T.subtitle}</p>
          </div>
        </h1>
        <Button onClick={openCreate} className="bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg">
          <Plus className="w-5 h-5 ml-2" />
          <span>{T.createNew}</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center"><Package className="w-5 h-5 text-orange-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalProducts}</p>
          <p className="text-2xl font-bold text-gray-900">{products.length}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Boxes className="w-5 h-5 text-blue-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalStock}</p>
          <p className="text-2xl font-bold text-blue-700">{totalStock.toLocaleString()}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><DollarSign className="w-5 h-5 text-green-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalValue}</p>
          <p className="text-2xl font-bold text-green-700">{totalValue.toLocaleString()} EGP</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center"><AlertCircle className="w-5 h-5 text-yellow-600" /></div></div>
          <p className="text-sm text-gray-500">{T.lowStock}</p>
          <p className="text-2xl font-bold text-yellow-700">{lowStockCount}</p>
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" placeholder={T.search} value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 transition" />
            </div>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500">
              <option value="all">جميع الفئات</option>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="p-12 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div><p className="text-gray-500 mt-4">{T.loading}</p></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center"><Package className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-900 mb-2">{T.noProducts}</h3></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thName}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thSku}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thPrice}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thCost}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thStock}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thCategory}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thActions}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((product) => {
                    const stockStatus = getStockStatus(product.stock);
                    const margin = getProfitMargin(product.price, product.cost);
                    return (
                      <tr key={product.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white"><Package className="w-5 h-5" /></div>
                            <div>
                              <p className="font-semibold text-gray-900">{product.name}</p>
                              <p className="text-xs text-gray-500">{T.profit}: {(product.price - product.cost).toFixed(2)} EGP ({margin}%)</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-5"><span className="font-mono text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded">{product.sku}</span></td>
                        <td className="py-4 px-5"><span className="font-bold text-green-600">{product.price.toFixed(2)} EGP</span></td>
                        <td className="py-4 px-5"><span className="text-gray-700">{product.cost.toFixed(2)} EGP</span></td>
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{product.stock}</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${stockStatus.color}`}>{stockStatus.label}</span>
                          </div>
                        </td>
                        <td className="py-4 px-5"><span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold">{product.category || '-'}</span></td>
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-1">
                            <button onClick={() => openView(product)} className="p-2 hover:bg-orange-50 rounded-lg text-gray-500 hover:text-orange-600 transition" title={T.view}><Eye className="w-4 h-4" /></button>
                            <button onClick={() => openEdit(product)} className="p-2 hover:bg-orange-50 rounded-lg text-gray-500 hover:text-orange-600 transition" title={T.edit}><Edit className="w-4 h-4" /></button>
                            <button onClick={() => handleDelete(product.id)} className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition" title={T.delete}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingProduct ? T.editProduct : T.createProduct}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><Label>{T.lblName} *</Label><Input value={form.name} onChange={(e: any) => setForm({...form, name: e.target.value})} required className={inputClass} /></div>
                <div><Label>{T.lblSku} *</Label><Input value={form.sku} onChange={(e: any) => setForm({...form, sku: e.target.value})} required className={inputClass} /></div>
                <div><Label>{T.lblCategory} *</Label><Input value={form.category} onChange={(e: any) => setForm({...form, category: e.target.value})} required className={inputClass} /></div>
                <div><Label>{T.lblPrice} *</Label><Input type="number" step="0.01" value={form.price} onChange={(e: any) => setForm({...form, price: e.target.value})} required className={inputClass} /></div>
                <div><Label>{T.lblCost} *</Label><Input type="number" step="0.01" value={form.cost} onChange={(e: any) => setForm({...form, cost: e.target.value})} required className={inputClass} /></div>
                <div><Label>{T.lblStock} *</Label><Input type="number" value={form.stock} onChange={(e: any) => setForm({...form, stock: e.target.value})} required className={inputClass} /></div>
                <div className="md:col-span-2"><Label>{T.lblDescription}</Label><textarea value={form.description} onChange={(e: any) => setForm({...form, description: e.target.value})} className={inputClass} rows={3} /></div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">{T.cancel}</Button>
                <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">{editingProduct ? T.update : T.create}</Button>
              </div>
            </form>
          </div></Card>
        </div>
      )}

      {showViewModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{T.viewProduct}</h2>
              <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-4 pb-4 border-b mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white"><Package className="w-8 h-8" /></div>
              <div><h3 className="text-xl font-bold">{selectedProduct.name}</h3><p className="text-gray-500 font-mono">{selectedProduct.sku}</p></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-sm text-gray-500">{T.thPrice}</p><p className="font-bold text-green-600 text-xl">{selectedProduct.price.toFixed(2)} EGP</p></div>
              <div><p className="text-sm text-gray-500">{T.thCost}</p><p className="font-semibold">{selectedProduct.cost.toFixed(2)} EGP</p></div>
              <div><p className="text-sm text-gray-500">{T.profit}</p><p className="font-semibold text-blue-600">{(selectedProduct.price - selectedProduct.cost).toFixed(2)} EGP</p></div>
              <div><p className="text-sm text-gray-500">{T.margin}</p><p className="font-semibold">{getProfitMargin(selectedProduct.price, selectedProduct.cost)}%</p></div>
              <div><p className="text-sm text-gray-500">{T.thStock}</p><p className="font-semibold">{selectedProduct.stock}</p></div>
              <div><p className="text-sm text-gray-500">{T.thCategory}</p><p className="font-semibold">{selectedProduct.category || '-'}</p></div>
              <div className="col-span-2"><p className="text-sm text-gray-500">{T.lblDescription}</p><p className="font-semibold">{selectedProduct.description || '-'}</p></div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={() => { setShowViewModal(false); openEdit(selectedProduct); }} className="flex-1 bg-orange-600 hover:bg-orange-700"><Edit className="w-4 h-4 ml-2" />{T.edit}</Button>
              <Button onClick={() => setShowViewModal(false)} variant="outline" className="flex-1">{T.close}</Button>
            </div>
          </div></Card>
        </div>
      )}
    </div>
  );
}
